// src/pages/PatientProfile/index.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { countActiveCases } from '../../lib/activeCaseRules';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import PatientInfoCard from './components/PatientInfoCard';
import MedicalHistoryCard from './components/MedicalHistoryCard';
import CaseHistoryCard from './components/CaseHistoryCard';
import RecentActivityCard from './components/RecentActivityCard';
import ActionButtons from './components/ActionButtons';
import DocumentsCard from './components/DocumentsCard';
import PatientEditModal from './components/PatientEditModal';
import { formatPatientDisplayName, formatPatientGender } from '../../lib/patientIdentity';
import { patientStatusLabel } from '../../lib/patientStatus';
import { fetchPatientSmileFrontPhotoUrl } from '../../lib/patientProfilePhoto';

const PatientProfile = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [casesData, setCasesData] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [currentPatient, setCurrentPatient] = useState(null);

  const fetchPatientCases = async (patientId) => {
    try {
      const { data: cases, error } = await supabase
        .from('cases')
        .select(`
          *,
          dentist:user_profiles!cases_user_profile_id_fkey(full_name),
          assigned_staff:user_profiles!cases_assigned_staff_id_fkey(full_name),
          current_phase_data:case_timeline_phases!inner(phase_name, status)
        `)
        .eq('patient_id', patientId)
        .order('created_date', { ascending: false });

      if (error) throw error;

      const formattedCases = cases.map(c => ({
        id: c.id,
        caseNumber: c.case_number,
        treatmentType: c.treatment_type || 'Type non défini',
        status: getCaseStatusLabel(c.status),
        currentPhase: c.current_phase || 'Phase non définie',
        createdDate: new Date(c.created_date).toLocaleDateString('fr-FR'),
        lastUpdate: new Date(c.updated_at).toLocaleDateString('fr-FR'),
        dentist: c.dentist?.full_name || 'Non assigné',
        urgent:
          c.priority === 'urgent' ||
          c.priority === 'urgente' ||
          c.priority === 'high' ||
          c.priority === 'express',
      }));

      setCasesData(formattedCases);

      const activeCount = countActiveCases(cases);
      setPatientData((prev) =>
        prev ? { ...prev, activeCases: activeCount, active_cases: activeCount } : prev,
      );
      setCurrentPatient((prev) =>
        prev ? { ...prev, activeCases: activeCount, active_cases: activeCount } : prev,
      );
    } catch (error) {
      console.error('Error fetching patient cases:', error);
    }
  };

  const getCaseStatusLabel = (status) => {
    const labels = {
      draft: 'Brouillon',
      submitted: 'Soumis',
      in_review: 'En Révision',
      in_progress: 'En Cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
      on_hold: 'En Attente',
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // ── Map raw DB row → shape used by PatientInfoCard ──────────────────────────
  const mapToPatientData = (data) => ({
    id: data.id,
    // Raw DB fields (used by PatientEditModal for saving)
    patient_code:      data.patient_code,
    name:              data.name,
    first_name:        data.first_name,
    last_name:         data.last_name,
    gender:            data.gender,
    initials:          data.initials,
    email:             data.email,
    phone:             data.phone,
    date_birth:        data.date_birth,
    location:          data.location,
    status:            data.status,
    active_cases:      data.active_cases ?? 0,
    registration_date: data.registration_date,
    last_visit:        data.last_visit,
    // Display-only fields (used by PatientInfoCard labels)
    patientCode:       data.patient_code,
    fullName:          formatPatientDisplayName(data),
    genderLabel:       formatPatientGender(data.gender),
    dateOfBirth:       formatDate(data.date_birth),
    address:           data.location,
    registrationDate:  formatDate(data.registration_date),
    lastVisit:         formatDate(data.last_visit),
    activeCases:       data.active_cases ?? 0,
    statusLabel:       patientStatusLabel(data.status),
    statusRaw:         data.status,
  });

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setCurrentUserId(user.id);
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const url = await fetchPatientSmileFrontPhotoUrl(patientId);
        if (cancelled) {
          if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
          return;
        }
        setAvatarUrl((prev) => {
          if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (err) {
        console.error('Error loading patient smile photo:', err);
        if (!cancelled) setAvatarUrl(null);
      }
    })();

    return () => {
      cancelled = true;
      setAvatarUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [patientId]);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const { data, error } = await supabase
          .from('patients')
          .select('*')
          .eq('id', patientId)
          .single();

        if (error) throw error;

        if (data) {
          const mapped = mapToPatientData(data);
          setPatientData(mapped);
          // ✅ FIX: sync currentPatient after data is loaded
          setCurrentPatient(mapped);
          await fetchPatientCases(data.id);
        }
      } catch (error) {
        console.error('Error fetching patient:', error);
      } finally {
        setLoading(false);
      }
    };

    if (patientId) fetchPatient();
  }, [patientId]);

  // ── Called by PatientEditModal after a successful save ──────────────────────
  // `updated` contains the raw DB fields that were written; we re-map them so
  // display labels stay in sync without a full refetch.
  const handlePatientSaved = (updated) => {
    const refreshed = mapToPatientData({
      // Start from currentPatient so we keep fields not touched by the modal
      ...currentPatient,
      ...updated,
    });
    setPatientData(refreshed);
    setCurrentPatient(refreshed);
  };

  const getAuthRoleTypes = () => {
    try {
      const stored = localStorage.getItem('authUser');
      if (!stored) return [];
      const authUser = JSON.parse(stored);
      return (
        authUser.roleTypes ||
        authUser.roles?.map((r) => r.role_type) ||
        (authUser.primaryRole ? [authUser.primaryRole] : []) ||
        []
      );
    } catch {
      return [];
    }
  };

  const roleTypes = getAuthRoleTypes();
  const isAdministrator = roleTypes.includes('administrator');
  const isAgentAdministratif = roleTypes.includes('agent_administratif');
  const canManageAllPatients =
    isAdministrator || isAgentAdministratif;
  const breadcrumbItems = canManageAllPatients
    ? [
        { label: 'Tableau de bord', path: '/dashboard', icon: 'Home' },
        { label: 'Liste des Patients', path: '/patients-list', icon: 'Users' },
        { label: patientData?.fullName || 'Patient', path: '/patient-profile' },
      ]
    : [
        { label: 'Espace dentiste', path: '/dentiste', icon: 'LayoutGrid' },
        { label: 'Mes commandes', path: '/cases-list', icon: 'ClipboardList' },
        { label: patientData?.fullName || 'Patient', path: '/patient-profile' },
      ];

  const handleSidebarToggle = () => setSidebarCollapsed(!sidebarCollapsed);
  const handleCreateCase    = () => navigate('/case-detail');
  const handleSendMessage   = () => console.log('Send message clicked');
  const handleDocumentUpload = (file) => console.log('Document uploaded:', file);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Patient non trouvé</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      <TopBar sidebarCollapsed={sidebarCollapsed} onSidebarToggle={handleSidebarToggle} />

      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">

              <PatientInfoCard
                patient={patientData}
                avatarUrl={avatarUrl}
                onEdit={() => setEditOpen(true)}
              />

              {/* ✅ Modal placed once, outside the card, at page level */}
              <PatientEditModal
                patient={currentPatient}
                isOpen={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={handlePatientSaved}
              />

              <CaseHistoryCard cases={casesData} />
            </div>

            <div className="space-y-6">
              <ActionButtons
                onCreateCase={handleCreateCase}
                onEditProfile={() => setEditOpen(true)}
                onSendMessage={handleSendMessage}
                patient={patientData}
                currentUserId={currentUserId}
              />

              
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PatientProfile;